/**
 * F18 — Tour onboarding multilivello v2: tipi e registry.
 *
 * 6 tour tematici:
 *  - welcome              (tutti i ruoli)
 *  - configurator-wall    (tutti tranne VIE in read-only)
 *  - configurator-ceiling (tutti tranne VIE in read-only)
 *  - estimates            (tutti tranne VIE in read-only)
 *  - materials            (OWN/ADM/SUP, no TEC/VIE)
 *  - admin                (OWN/ADM, no SUP/TEC/VIE)
 *
 * Vedi `Volt/06_TASKS/F18-tour-multilivello-plan.md` per spec completa.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Sparkles,
  Layers,
  PanelTop,
  FileText,
  Package,
  Settings as SettingsIcon,
} from 'lucide-react';

// ============================================================================
// Tipi base
// ============================================================================

/** Identificatore univoco di un tour. */
export type TourId =
  | 'welcome'
  | 'configurator-wall'
  | 'configurator-ceiling'
  | 'estimates'
  | 'materials'
  | 'admin';

/** Ruolo dell'utente nell'organizzazione (vedi team-roles-matrix.md). */
export type OrgRole = 'owner' | 'admin' | 'supervisor' | 'technician' | 'viewer';

/** Modalità di esecuzione di un tour. */
export type TourMode = 'full' | 'readonly';

/** Stato di un tour per l'utente corrente. */
export type TourStatus = 'untouched' | 'completed' | 'dismissed';

/**
 * Singolo step di un tour. Mappato 1:1 a `Driver.driveStep` di driver.js,
 * più alcuni campi di nostro convenience (navigate, mode-specific text).
 */
export interface TourStep {
  /** CSS selector dell'elemento da evidenziare. Default convenzione: `[data-tour-id="..."]`. */
  element: string;
  /** Titolo del popover. Mostrato in bold. */
  title: string;
  /**
   * Descrizione del popover. Supporta HTML semplice (per **bold**, *italic*, link).
   * Se serve testo diverso in modalità sola lettura per VIE, usa `descriptionReadonly`.
   */
  description: string;
  /** Override description per `mode='readonly'`. Se null usa `description`. */
  descriptionReadonly?: string;
  /** Posizione del popover rispetto all'elemento. Driver.js default: 'over'. */
  side?: 'top' | 'bottom' | 'left' | 'right' | 'over';
  /**
   * Se settato, naviga a questa rotta prima dello step. Usato raramente —
   * preferiamo tour mono-route. Se il selettore non è presente all'arrivo,
   * il tour aspetta `waitForSelectorMs` poi salta lo step.
   */
  navigate?: string;
  /** Tempo max di attesa selettore dopo navigate (ms). Default: 1500. */
  waitForSelectorMs?: number;
  /** Disable interaction con la pagina sotto durante lo step. Default: false. */
  disableInteraction?: boolean;
}

/**
 * Metadata di un tour. Mostrato in `/profile` come card.
 */
export interface TourMeta {
  id: TourId;
  /** Titolo visualizzato nella UI (es. card in /profile). */
  title: string;
  /** Una riga di descrizione (≤ 100 char) per la card. */
  description: string;
  /** Icona Lucide mostrata accanto al titolo. */
  icon: LucideIcon;
  /** Durata stimata in minuti per la UI ("circa 3 min"). */
  estimatedMinutes: number;
  /** Ruoli che vedono il tour in modalità completa (azioni edit visibili). */
  fullRoles: OrgRole[];
  /** Ruoli che vedono il tour in modalità sola lettura (con banner disclaimer). */
  readOnlyRoles: OrgRole[];
  /**
   * Generatore degli step. Riceve il `mode` corrente per scegliere
   * `description` vs `descriptionReadonly`.
   */
  getSteps: (mode: TourMode) => TourStep[];
}

// ============================================================================
// Tour registry — popolato in F18.4-F18.8 con i getSteps reali.
// In F18.2 contiene solo meta + getSteps placeholder che ritornano [].
// ============================================================================

/**
 * Registry centrale dei tour. Aggiungere un nuovo tour = aggiungere una voce qui
 * con l'`id` corrispondente in `TourId`.
 *
 * I `getSteps` reali vengono importati e composti in `tours/<tourId>.ts` —
 * questo registry tiene solo il meta per evitare dipendenze circolari.
 */
export const TOUR_META: Record<TourId, Omit<TourMeta, 'getSteps'>> = {
  'welcome': {
    id: 'welcome',
    title: 'Benvenuto in DryConfig',
    description: 'Overview rapida dell\'app. Dove cliccare per iniziare.',
    icon: Sparkles,
    estimatedMinutes: 1,
    fullRoles: ['owner', 'admin', 'supervisor', 'technician', 'viewer'],
    readOnlyRoles: [],
  },
  'configurator-wall': {
    id: 'configurator-wall',
    title: 'Configura pareti a secco',
    description: 'Crea una stratigrafia parete: composizione, costi, scheda tecnica, preventivo.',
    icon: Layers,
    estimatedMinutes: 2,
    fullRoles: ['owner', 'admin', 'supervisor', 'technician'],
    readOnlyRoles: ['viewer'],
  },
  'configurator-ceiling': {
    id: 'configurator-ceiling',
    title: 'Configura controsoffitti',
    description: 'Pendinatura, orditura doppia, perimetro: tutto il flusso ceiling.',
    icon: PanelTop,
    estimatedMinutes: 2,
    fullRoles: ['owner', 'admin', 'supervisor', 'technician'],
    readOnlyRoles: ['viewer'],
  },
  'estimates': {
    id: 'estimates',
    title: 'Preventivi & CRM',
    description: 'Clienti, progetti, preventivi, workflow Bozza→Vinto, follow-up agenda.',
    icon: FileText,
    estimatedMinutes: 2,
    fullRoles: ['owner', 'admin', 'supervisor', 'technician'],
    readOnlyRoles: ['viewer'],
  },
  'materials': {
    id: 'materials',
    title: 'Catalogo & impostazioni tecniche',
    description: 'Materiali, import listini, sfridi, livelli finitura, viti auto-suggest.',
    icon: Package,
    estimatedMinutes: 2,
    fullRoles: ['owner', 'admin', 'supervisor'],
    readOnlyRoles: [],
  },
  'admin': {
    id: 'admin',
    title: 'Amministrazione organizzazione',
    description: 'Membri & ruoli, billing, branding PDF dei preventivi.',
    icon: SettingsIcon,
    estimatedMinutes: 1.5,
    fullRoles: ['owner', 'admin'],
    readOnlyRoles: [],
  },
};

/** Lista ordinata dei tour (priorità nella UI). */
export const TOUR_ORDER: TourId[] = [
  'welcome',
  'configurator-wall',
  'configurator-ceiling',
  'estimates',
  'materials',
  'admin',
];

// ============================================================================
// Utility
// ============================================================================

/** True se l'utente con `role` può vedere il tour (full O readonly). */
export function isTourVisible(meta: Omit<TourMeta, 'getSteps'>, role: OrgRole): boolean {
  return meta.fullRoles.includes(role) || meta.readOnlyRoles.includes(role);
}

/** Modalità in cui l'utente con `role` vede il tour. Null se non visibile. */
export function getTourMode(meta: Omit<TourMeta, 'getSteps'>, role: OrgRole): TourMode | null {
  if (meta.fullRoles.includes(role)) return 'full';
  if (meta.readOnlyRoles.includes(role)) return 'readonly';
  return null;
}

/** Stato del tour per un utente, dato il suo profilo. */
export function getTourStatus(
  tourId: TourId,
  completed: string[] = [],
  dismissed: string[] = [],
): TourStatus {
  if (completed.includes(tourId)) return 'completed';
  if (dismissed.includes(tourId)) return 'dismissed';
  return 'untouched';
}
